import React from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { Menu, Table } from 'antd';
import { ProhibitedArea } from 'components';
import useDataTagManager from '../../../hooks/useDataTagManager';
import ActiveFiltersDisplay from '../../molecules/ActiveFiltersDisplay';
import DataTagActionBar from '../../molecules/DataTagActionBar';
import DataTagFilterBar from '../../molecules/DataTagFilterBar';
import DataTagCardGrid from '../DataTagCardGrid';
import DataTagTable from '../DataTagTable';

const DataTagViewer = ({
  dataTagList,
  onCreateNewDataTag,
  onDeleteDataTag,
  onViewDataTag,
  onEditDataTag,
  onViewDataTagHistory,
  onMultiDeleteDataTags,
  onBulkMoveDataTags,
  permission,
  onTagReorder,
  onViewChange,
  viewState,
  generateTagsWithAI,
  createTags,
  rephraseDataTag,
  isGeneratingTags,
  isCreatingTags,
  isRephrasingTags,
  rephrasedDataTag,
  aiGeneratedTags,
  resetGeneratedTags,
  businessAreaList,
  bulkCreateErrors,
  aiGeneratedErrors,
  openAIKeyExists
}) => {
  const {
    tags,
    filteredTags,
    businessAreas,
    updatedByUsers,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    sortConfig,
    setSortConfig,
    pinnedTags,
    showPinnedOnly,
    setShowPinnedOnly,
    handlePinTag,
    selectedRowKeys,
    setSelectedRowKeys,
    onDragEnd,
    clearSorting,
    getBusinessAreaColors,
    isFilterActive,
    isSortActive,
  } = useDataTagManager(dataTagList, onTagReorder);

  if (!permission) {
    return <ProhibitedArea />;
  }

  const filterMenu = [
    {
      key: 'businessArea',
      label: 'Business Area',
      children: businessAreas.map(area => ({
        key: area,
        label: (
          <span>
            {area} {filters.businessArea === area && <CheckOutlined className="ml-2" />}
          </span>
        ),
        onClick: () => setFilters(prev => ({...prev, businessArea: prev.businessArea === area ? '' : area})),
      })),
    },
    {
      key: 'updatedBy',
      label: 'Updated By',
      children: updatedByUsers.map(user => ({
        key: user,
        label: (
          <span>
            {user} {filters.updatedBy === user && <CheckOutlined className="ml-2" />}
          </span>
        ),
        onClick: () => setFilters(prev => ({...prev, updatedBy: prev.updatedBy === user ? '' : user})),
      })),
    },
    {
      key: 'lastUpdated',
      label: 'Last Updated',
      children: ['Today', 'This-Week', 'This-Month'].map(period => ({
        key: period,
        label: (
          <span>
            {period} {filters.lastUpdated === period && <CheckOutlined className="ml-2" />}
          </span>
        ),
        onClick: () => setFilters(prev => ({...prev, lastUpdated: prev.lastUpdated === period ? '' : period})),
      })),
    },
  ];

  const sortMenu = [
    {
      key: 'tagName',
      label: `Tag Name ${sortConfig.key === 'tagName' ? (sortConfig.direction === 'ascend' ? '↑' : '↓') : ''}`,
      onClick: () => setSortConfig({ key: 'tagName', direction: sortConfig.direction === 'ascend' ? 'descend' : 'ascend' }),
    },
    {
      key: 'businessAreaName',
      label: `Business Area ${sortConfig.key === 'businessAreaName' ? (sortConfig.direction === 'ascend' ? '↑' : '↓') : ''}`,
      onClick: () => setSortConfig({ key: 'businessAreaName', direction: sortConfig.direction === 'ascend' ? 'descend' : 'ascend' }),
    },
    {
      key: 'updatedDate',
      label: `Last Updated ${sortConfig.key === 'updatedDate' ? (sortConfig.direction === 'ascend' ? '↑' : '↓') : ''}`,
      onClick: () => setSortConfig({ key: 'updatedDate', direction: sortConfig.direction === 'ascend' ? 'descend' : 'ascend' }),
    },
  ];
  
  return (
  <div className="h-5/6 p-1">
      <div className="flex flex-col md:flex-row justify-between items-center mb-3 space-y-2 md:space-y-0">
        <DataTagFilterBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterMenu={filterMenu}
          isFilterActive={isFilterActive}
          filters={filters}
          showPinnedOnly={showPinnedOnly}
          filteredCount={filteredTags.length}
          totalCount={tags.length}
          viewState={viewState}
          sortMenu={sortMenu}
          isSortActive={isSortActive}
          pinnedTags={pinnedTags}
          setShowPinnedOnly={setShowPinnedOnly}
        />
        <DataTagActionBar
          selectedRowKeys={selectedRowKeys}
          onMultiDeleteDataTags={onMultiDeleteDataTags}
          setSelectedRowKeys={setSelectedRowKeys}
          totalTagsCount={filteredTags.length}
          onBulkMoveDataTags={onBulkMoveDataTags}
          aiProps={{
            generateTagsWithAI,
            createTags,
            isGeneratingTags,
            isCreatingTags,
            businessAreaList,
            aiGeneratedTags,
            resetGeneratedTags: resetGeneratedTags,
            aiGeneratedErrors,
            bulkCreateErrors,
            openAIKeyExists,
            dataTagList,
            rephraseDataTag,
            isRephrasingTags,
            rephrasedDataTag,
          }}
          onCreateNewDataTag={onCreateNewDataTag}
          permission={permission}
          onViewChange={onViewChange}
          viewState={viewState}
        />
      </div>
      <ActiveFiltersDisplay
        filters={filters}
        showPinnedOnly={showPinnedOnly}
        setFilters={setFilters}
        setShowPinnedOnly={setShowPinnedOnly}
        sortConfig={sortConfig}
        clearSorting={clearSorting}
        totalCount={tags.length}
      />
      {viewState === 'card' ? (
        <DataTagCardGrid
          filteredTags={filteredTags}
          allTags={tags}
          onDragEnd={onDragEnd}
          pinnedTags={pinnedTags}
          handlePinTag={handlePinTag}
          permission={permission}
          onEditDataTag={onEditDataTag}
          onViewDataTag={onViewDataTag}
          onViewDataTagHistory={onViewDataTagHistory}
          onDeleteDataTag={onDeleteDataTag}
          getBusinessAreaColors={getBusinessAreaColors}
          onCreateNewDataTag={onCreateNewDataTag}
        />
      ) : (
        <DataTagTable
          filteredTags={filteredTags}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
          pinnedTags={pinnedTags}
          handlePinTag={handlePinTag}
          permission={permission}
          onEditDataTag={onEditDataTag}
          onViewDataTag={onViewDataTag}
          onViewDataTagHistory={onViewDataTagHistory}
          onDeleteDataTag={onDeleteDataTag}
          sortConfig={sortConfig}
          setSortConfig={setSortConfig}
        />
      )}
    </div>
  );
};
export default React.memo(DataTagViewer);